/* ============================================================
   dbakers Mini Cupcake Builder JS — Accordion Style
   ADA compliant — aria-expanded, aria-live, disabled guards

   Adapted from dbakers-signature-builder.js (2026-05-21 baseline).
   Same two-step submit pattern, same companion-line tagging via
   _addon_for, same verify step.

   Key differences vs signature:
   - No size variants — product has one variant, one fixed price
   - No dietary step — allergen badges live on flavor cards
   - Flavor picker state: { flavor1, flavor2 } strings, both required
   - "Make it special" only has candles + gift message
   - No plaque, no topper, no extras (bow/server/coldpack)
   ============================================================ */

(function () {
  'use strict';

  /* Pull variant ID maps from Liquid. Same defensive defaults as
     signature — affected add-ons silently no-op if a map is missing
     instead of breaking the whole builder. */
  var ADDON_IDS    = window.dbmcbAddonIds        || {};
  var CANDLE_IDS   = window.dbmcbCandleVariants  || {};
  var METALLIC_IDS = window.dbmcbMetallicVariants || {};

  var state = {
    variantId: null,
    basePrice: 0,
    productTitle: '',
    flavors: {
      flavor1: null,  /* { name, allergens: [...], gfEligible: bool } */
      flavor2: null,
      activeSlot: null  /* 'flavor1' | 'flavor2' | null — which slot the grid is filling */
    },
    /* Dietary toggles. Both can be active simultaneously. Each has
       its own acknowledgment that must be checked before the section
       counts as "done" — though dietary is optional, so customers can
       skip it entirely and just go to "Make it special". */
    diet: {
      gf: false,            /* +$15 upcharge, hides non-gf-eligible flavors */
      gfAcknowledged: false,
      nut: false,           /* no charge, hides nut-containing flavors */
      nutAcknowledged: false
    },
    addons: {
      message: { selected: false, text: '' },  /* gift message (free, no plaque) */
      candles: {
        selected: false,
        types: {
          rainbow:   { active: false },
          sparklers: { active: false },
          metallic:  { active: false, color: '' },
          number:    { active: false, color: '', number: '' }
        }
      }
    }
  };

  function init() {
    if (!document.getElementById('dbmcb-root')) return;

    /* Read base product info exported by Liquid. We use this product's
       single variant — no size picker means the variant is fixed at
       load time. */
    if (window.dbakersMiniCupcakeProduct) {
      state.variantId    = window.dbakersMiniCupcakeProduct.variantId;
      state.basePrice    = window.dbakersMiniCupcakeProduct.basePrice / 100;
      state.productTitle = window.dbakersMiniCupcakeProduct.title || '';
    }

    bindSectionHeaders();
    bindSlots();
    bindFlavorCards();
    bindInfoButtons();
    bindDescPopover();
    bindDietaryToggles();
    bindDietarySkip();
    bindAcknowledgments();
    bindAddonRows();
    bindCandleTypes();
    bindImageTiles();
    bindGiftInput();
    bindCandleNumber();
    bindStickyBtn();
    updateSticky();
    openSection('diet');
  }

  if (document.readyState === 'complete') {
    setTimeout(init, 300);
  } else {
    window.addEventListener('load', function () { setTimeout(init, 300); });
  }

  /* ── Accordion ── */
  function openSection(id) {
    var body = document.getElementById('dbmcb-body-' + id);
    var header = document.querySelector('[data-section="' + id + '"]');
    if (body) body.classList.add('is-open');
    if (header) header.setAttribute('aria-expanded', 'true');
  }
  function closeSection(id) {
    var body = document.getElementById('dbmcb-body-' + id);
    var header = document.querySelector('[data-section="' + id + '"]');
    if (body) body.classList.remove('is-open');
    if (header) header.setAttribute('aria-expanded', 'false');
  }
  function bindSectionHeaders() {
    document.querySelectorAll('.dbsb-section-header').forEach(function (header) {
      header.addEventListener('click', function (e) {
        e.stopPropagation();
        var id   = this.dataset.section;
        var body = document.getElementById('dbmcb-body-' + id);
        if (body) {
          body.classList.toggle('is-open');
          this.setAttribute('aria-expanded', body.classList.contains('is-open') ? 'true' : 'false');
        }
      });
      header.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
      });
    });
  }

  /* ── Slot picker ──
     Clicking a slot:
     - Sets it as the active slot (highlights it)
     - Opens the flavor grid below
     - Updates the grid's "filling Flavor X" prompt
     - Marks any currently-selected flavor card as selected if it
       matches the slot's current value

     This is intentionally simple — one shared grid, the active slot
     tells the grid which slot it's filling. Simpler than rendering
     two side-by-side grids. */
  function bindSlots() {
    document.querySelectorAll('.dbsb-slot').forEach(function (slot) {
      slot.addEventListener('click', function (e) {
        e.preventDefault();
        var slotKey = this.dataset.slot;
        if (!slotKey) return;
        activateSlot(slotKey);
      });
      slot.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
      });
    });
  }

  function activateSlot(slotKey) {
    state.flavors.activeSlot = slotKey;
    document.querySelectorAll('.dbsb-slot').forEach(function (el) {
      el.classList.toggle('is-active', el.dataset.slot === slotKey);
    });

    var wrap = document.getElementById('dbmcb-flavor-grid-wrap');
    if (wrap) wrap.classList.add('is-visible');

    var prompt = document.getElementById('dbmcb-flavor-grid-prompt');
    if (prompt) {
      prompt.textContent = slotKey === 'flavor1'
        ? 'Pick Flavor 1 (12 cupcakes)'
        : 'Pick Flavor 2 (12 cupcakes)';
    }

    /* Highlight whichever flavor card matches the slot's current value
       (or none if slot is empty). */
    var currentValue = state.flavors[slotKey] ? state.flavors[slotKey].name : null;
    document.querySelectorAll('.dbsb-flavor-card').forEach(function (card) {
      card.classList.toggle('is-selected', card.dataset.flavorName === currentValue);
    });

    /* Scroll the grid into view so the customer can see it. Smooth
       scroll, block: 'center' so they don't have to hunt for it. */
    setTimeout(function () {
      if (wrap) wrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }

  /* ── Flavor cards ──
     Click → set the active slot's flavor, render the slot's filled
     state, mark step done if both slots filled, and auto-advance to
     the "Make it special" section.

     Same flavor in both slots is intentionally allowed — customer can
     do 24 of one flavor (12 + 12). The picker doesn't disable cards
     based on the other slot. */
  function bindFlavorCards() {
    document.querySelectorAll('.dbsb-flavor-card').forEach(function (card) {
      card.addEventListener('click', function (e) {
        e.preventDefault();
        if (!state.flavors.activeSlot) {
          /* Defensive: if no slot is active, default to flavor1 if empty,
             else flavor2. Customer shouldn't normally hit this since
             the grid is hidden until a slot is clicked. */
          activateSlot(state.flavors.flavor1 ? 'flavor2' : 'flavor1');
        }

        var flavor = {
          name:       this.dataset.flavorName,
          allergens:  (this.dataset.flavorAllergens || '').split(',').filter(Boolean),
          gfEligible: this.dataset.flavorGfEligible !== 'false'
        };
        var slotKey = state.flavors.activeSlot;
        state.flavors[slotKey] = flavor;

        renderSlot(slotKey);

        /* Highlight just this card. */
        document.querySelectorAll('.dbsb-flavor-card').forEach(function (c) {
          c.classList.remove('is-selected');
        });
        this.classList.add('is-selected');

        /* If both slots filled → mark section done. Auto-advance to
           "special" section so the customer keeps moving. If only one
           slot filled, switch the active slot to the empty one so the
           next click on a flavor card fills that slot (less tapping). */
        if (state.flavors.flavor1 && state.flavors.flavor2) {
          markSectionDone('flavors');
          state.flavors.activeSlot = null;
          document.querySelectorAll('.dbsb-slot').forEach(function (s) { s.classList.remove('is-active'); });
          var wrap = document.getElementById('dbmcb-flavor-grid-wrap');
          if (wrap) wrap.classList.remove('is-visible');
          var stickyBtn = document.getElementById('dbmcb-sticky-btn');
          if (stickyBtn) stickyBtn.classList.add('is-active');
          updateSticky();
          closeSection('flavors');
          openSection('special');
        } else {
          /* One slot still empty — switch active to the empty one. */
          var nextSlot = !state.flavors.flavor1 ? 'flavor1' : 'flavor2';
          activateSlot(nextSlot);
          updateSticky();
        }
      });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
      });
    });
  }

  /* ── Info (?) buttons on flavor cards (mobile only via CSS) ──
     stopPropagation is critical: without it, clicking ? would bubble
     to the parent .dbsb-flavor-card button and pick that flavor when
     the customer just wanted to read its description. */
  function bindInfoButtons() {
    document.querySelectorAll('.dbsb-flavor-info-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        var desc = btn.dataset.desc || '';
        var name = btn.dataset.flavorName || '';
        openDescPopover(name, desc);
      });
      btn.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          var desc = btn.dataset.desc || '';
          var name = btn.dataset.flavorName || '';
          openDescPopover(name, desc);
        }
      });
    });
  }

  /* ── Description popover (mobile flavor details) ── */
  function bindDescPopover() {
    var pop = document.getElementById('dbmcb-desc-popover');
    if (!pop) return;
    var closeBtn = document.getElementById('dbmcb-desc-popover-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', closeDescPopover);
    }
    /* Tap on the popover backdrop (dim area outside inner card) closes it.
       Tap on the inner card does not. */
    pop.addEventListener('click', function (e) {
      if (e.target === pop) closeDescPopover();
    });
    /* Esc to close */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !pop.hidden) closeDescPopover();
    });
  }

  function openDescPopover(name, desc) {
    var pop = document.getElementById('dbmcb-desc-popover');
    var titleEl = document.getElementById('dbmcb-desc-popover-title');
    var bodyEl = document.getElementById('dbmcb-desc-popover-body');
    if (!pop || !titleEl || !bodyEl) return;
    titleEl.textContent = name;
    bodyEl.textContent = desc;
    pop.hidden = false;
    var closeBtn = document.getElementById('dbmcb-desc-popover-close');
    if (closeBtn) closeBtn.focus();
  }

  function closeDescPopover() {
    var pop = document.getElementById('dbmcb-desc-popover');
    if (pop) pop.hidden = true;
  }

  /* Render a single slot's filled state. Called whenever a flavor is
     picked. Falls back to empty state if state.flavors[slotKey] is
     null. */
  function renderSlot(slotKey) {
    var slot = document.querySelector('[data-slot="' + slotKey + '"]');
    if (!slot) return;
    var flavor = state.flavors[slotKey];
    var labelText = slotKey === 'flavor1' ? 'FLAVOR 1 · 12 CUPCAKES' : 'FLAVOR 2 · 12 CUPCAKES';

    /* Text-only slot rendering — no thumbnail since we don't have
       per-flavor mini-cupcake photos. Filled state shows the flavor
       name + a "Tap to change" hint; empty state shows the slot label
       + a "Tap to choose…" prompt. */
    if (flavor) {
      slot.classList.add('is-filled');
      slot.classList.remove('is-active');
      slot.innerHTML =
        '<div class="dbsb-slot-content">' +
          '<div class="dbsb-slot-label">' + labelText + '</div>' +
          '<div class="dbsb-slot-name">' + escapeHtml(flavor.name) + '</div>' +
          '<div class="dbsb-slot-hint">Tap to change</div>' +
        '</div>';
    } else {
      slot.classList.remove('is-filled');
      slot.innerHTML =
        '<div class="dbsb-slot-content">' +
          '<div class="dbsb-slot-label">' + labelText + '</div>' +
          '<div class="dbsb-slot-empty-text">Tap to choose…</div>' +
        '</div>';
    }
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ──────────────────────────────────────────────────────────
     DIETARY TOGGLES — GF + Nut-Free

     Both toggles are independent (a customer can pick both). When
     either toggle is activated:
     - The corresponding acknowledgment disclaimer appears
     - The flavor grid is re-filtered (some cards hidden via .is-hidden)
     - Any picked flavor that's no longer available is cleared, and
       a live-region message tells the customer what happened
     ────────────────────────────────────────────────────────── */
  function bindDietaryToggles() {
    var gfToggle  = document.getElementById('dbmcb-toggle-gf');
    var nutToggle = document.getElementById('dbmcb-toggle-nut');

    if (gfToggle) {
      gfToggle.addEventListener('click', function (e) {
        e.preventDefault();
        var nowOn = !state.diet.gf;
        state.diet.gf = nowOn;
        state.diet.gfAcknowledged = false;  /* reset ack on every toggle change */
        this.setAttribute('aria-checked', String(nowOn));

        var warn = document.getElementById('dbmcb-gf-warning');
        var box  = document.getElementById('dbmcb-gf-box');
        if (warn) warn.classList.toggle('is-visible', nowOn);
        if (box)  box.classList.remove('is-checked');

        applyDietaryFilters();
        updateSticky();
      });
      gfToggle.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
      });
    }

    if (nutToggle) {
      nutToggle.addEventListener('click', function (e) {
        e.preventDefault();
        var nowOn = !state.diet.nut;
        state.diet.nut = nowOn;
        state.diet.nutAcknowledged = false;
        this.setAttribute('aria-checked', String(nowOn));

        var warn = document.getElementById('dbmcb-nut-warning');
        var box  = document.getElementById('dbmcb-nut-box');
        if (warn) warn.classList.toggle('is-visible', nowOn);
        if (box)  box.classList.remove('is-checked');

        applyDietaryFilters();
        updateSticky();
      });
      nutToggle.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
      });
    }
  }

  /* Re-evaluate which flavor cards are visible based on current toggle
     state. Also handles clearing any already-picked flavor that's no
     longer available, with a visible message so the customer knows
     why their selection changed (better than silently dropping it). */
  function applyDietaryFilters() {
    var cards = document.querySelectorAll('.dbsb-flavor-card');
    cards.forEach(function (card) {
      var allergens = (card.dataset.flavorAllergens || '').split(',').filter(Boolean);
      var gfEligible = card.dataset.flavorGfEligible !== 'false';

      var hidden = false;
      if (state.diet.gf && !gfEligible)         hidden = true;
      if (state.diet.nut && allergens.indexOf('nuts') !== -1) hidden = true;

      card.classList.toggle('is-hidden', hidden);
      if (hidden) card.setAttribute('aria-hidden', 'true');
      else card.removeAttribute('aria-hidden');
    });

    /* If a previously-picked flavor is now hidden, clear it. */
    var cleared = [];
    ['flavor1', 'flavor2'].forEach(function (slotKey) {
      var f = state.flavors[slotKey];
      if (!f) return;
      var nowUnavailable =
        (state.diet.gf  && f.gfEligible === false) ||
        (state.diet.nut && f.allergens && f.allergens.indexOf('nuts') !== -1);
      if (nowUnavailable) {
        cleared.push(f.name);
        state.flavors[slotKey] = null;
        renderSlot(slotKey);
        markSectionUndone('flavors');
      }
    });

    /* Show the live message if we cleared anything; otherwise hide it. */
    var msgEl = document.getElementById('dbmcb-flavor-cleared-msg');
    if (msgEl) {
      if (cleared.length > 0) {
        var reason;
        if (state.diet.gf && state.diet.nut) reason = 'with these dietary needs';
        else if (state.diet.gf)              reason = 'as gluten-free';
        else if (state.diet.nut)             reason = 'nut-free';
        else                                 reason = 'with these dietary needs';

        msgEl.textContent = (cleared.length === 1
          ? '“' + cleared[0] + '” isn\'t available ' + reason + ' — please pick again.'
          : 'Some of your flavor picks aren\'t available ' + reason + ' — please pick again.');
        msgEl.classList.add('is-visible');
      } else {
        msgEl.classList.remove('is-visible');
      }
    }
  }

  /* ── Dietary skip ── "No dietary needs" link at bottom of diet section.
     Closes diet, opens flavors. Doesn't reset any toggle state in case
     the customer comes back. */
  function bindDietarySkip() {
    var skip = document.getElementById('dbmcb-diet-skip');
    if (!skip) return;
    skip.addEventListener('click', function (e) {
      e.preventDefault();
      markSectionDone('diet');
      closeSection('diet');
      openSection('flavors');
      /* Scroll into view so the customer sees the next step opened. */
      var section = document.getElementById('dbmcb-section-flavors');
      if (section) section.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    skip.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
    });
  }

  /* ── GF + Nut acknowledgments — same pattern as signature builder.
     mousedown + touchstart fire before Dawn's click handlers so the
     theme doesn't swallow the event. */
  function bindAcknowledgments() {
    var gfAck = document.getElementById('dbmcb-gf-ack');
    if (gfAck) {
      var handleGf = function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        var box = document.getElementById('dbmcb-gf-box');
        if (!box) return;
        box.classList.toggle('is-checked');
        state.diet.gfAcknowledged = box.classList.contains('is-checked');
        gfAck.setAttribute('aria-checked', String(state.diet.gfAcknowledged));
        checkDietaryDone();
        updateSticky();
      };
      gfAck.addEventListener('mousedown', handleGf);
      gfAck.addEventListener('touchstart', handleGf, { passive: false });
      gfAck.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleGf(e); }
      });
    }

    var nutAck = document.getElementById('dbmcb-nut-ack');
    if (nutAck) {
      var handleNut = function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        var box = document.getElementById('dbmcb-nut-box');
        if (!box) return;
        box.classList.toggle('is-checked');
        state.diet.nutAcknowledged = box.classList.contains('is-checked');
        nutAck.setAttribute('aria-checked', String(state.diet.nutAcknowledged));
        checkDietaryDone();
        updateSticky();
      };
      nutAck.addEventListener('mousedown', handleNut);
      nutAck.addEventListener('touchstart', handleNut, { passive: false });
      nutAck.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNut(e); }
      });
    }
  }

  /* Mark dietary section "done" when:
     - No toggles are on (section is optional — customer can ignore it), OR
     - All active toggles have their acknowledgments checked

     When the customer JUST finished acknowledging (transitioned from
     needing-ack to all-acked), auto-advance to flavors. We only do
     this if no flavor has been picked yet — otherwise the customer
     is editing dietary after-the-fact and we shouldn't reshuffle. */
  function checkDietaryDone() {
    var hasAnyToggle  = state.diet.gf || state.diet.nut;
    var gfOk          = !state.diet.gf  || state.diet.gfAcknowledged;
    var nutOk         = !state.diet.nut || state.diet.nutAcknowledged;
    var allAcked      = gfOk && nutOk;
    var noFlavorsYet  = !state.flavors.flavor1 && !state.flavors.flavor2;

    if (allAcked) {
      markSectionDone('diet');
      /* Auto-advance to flavors only on the first-time path: a toggle
         is on, ack just got checked, and no flavors are picked yet. */
      if (hasAnyToggle && noFlavorsYet) {
        closeSection('diet');
        openSection('flavors');
      }
    } else {
      markSectionUndone('diet');
    }
  }

  function markSectionUndone(id) {
    var num = document.getElementById('dbmcb-num-' + id);
    if (num) num.classList.remove('is-done');
  }

  /* ── Addon rows ── */
  function bindAddonRows() {
    document.querySelectorAll('.dbsb-addon-row').forEach(function (row) {
      row.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        var id    = this.dataset.addon;
        var card  = document.getElementById('dbmcb-addon-card-' + id);
        var panel = document.getElementById('dbmcb-sub-panel-' + id);
        if (!card) return;
        var isOpen = card.classList.contains('is-selected');
        card.classList.toggle('is-selected', !isOpen);
        if (panel) {
          panel.classList.toggle('is-open', !isOpen);
          panel.setAttribute('aria-hidden', String(isOpen));
        }
        this.setAttribute('aria-expanded', String(!isOpen));
        if (state.addons[id]) state.addons[id].selected = !isOpen;
        if (isOpen) resetAddon(id);
        updateSticky();
      });
      row.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
      });
    });
  }

  function resetAddon(id) {
    if (id === 'message') {
      state.addons.message.text = '';
      var ta = document.getElementById('dbmcb-message-text');
      if (ta) ta.value = '';
    }
    if (id === 'candles') {
      state.addons.candles.types.rainbow.active   = false;
      state.addons.candles.types.sparklers.active = false;
      state.addons.candles.types.metallic.active  = false;
      state.addons.candles.types.metallic.color   = '';
      state.addons.candles.types.number.active    = false;
      state.addons.candles.types.number.color     = '';
      state.addons.candles.types.number.number    = '';
      document.querySelectorAll('.dbsb-type-pill').forEach(function (p) {
        p.classList.remove('is-selected');
        p.setAttribute('aria-checked', 'false');
      });
      document.querySelectorAll('.dbsb-candle-sub').forEach(function (el) {
        el.classList.remove('is-visible');
      });
      document.querySelectorAll('[data-tile-group^="candle-color-"]').forEach(function (t) {
        t.classList.remove('is-selected');
        t.setAttribute('aria-checked', 'false');
      });
      var cn = document.getElementById('dbmcb-candle-number');
      if (cn) cn.value = '';
    }
  }

  /* ── Candle type pills — multi-select (rainbow + sparklers + metallic + number all OK) ── */
  function bindCandleTypes() {
    document.querySelectorAll('.dbsb-type-pill').forEach(function (pill) {
      pill.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        var type = this.dataset.type;
        var typeState = state.addons.candles.types[type];
        if (!typeState) return;

        typeState.active = !typeState.active;
        this.classList.toggle('is-selected', typeState.active);
        this.setAttribute('aria-checked', typeState.active ? 'true' : 'false');

        var sub = document.getElementById('dbmcb-candle-sub-' + type);
        if (sub) sub.classList.toggle('is-visible', typeState.active);

        if (!typeState.active) {
          if (type === 'metallic') typeState.color = '';
          if (type === 'number')   { typeState.color = ''; typeState.number = ''; }
          document.querySelectorAll('[data-tile-group="candle-color-' + type + '"]').forEach(function (t) {
            t.classList.remove('is-selected');
            t.setAttribute('aria-checked', 'false');
          });
          if (type === 'number') {
            var cn = document.getElementById('dbmcb-candle-number');
            if (cn) cn.value = '';
          }
        }
        updateSticky();
      });
      pill.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
      });
    });
  }

  /* ── Image tiles (metallic / number candle colors) ── */
  function bindImageTiles() {
    document.querySelectorAll('.dbsb-img-tile').forEach(function (tile) {
      tile.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        var group = this.dataset.tileGroup;
        if (!group) return;
        document.querySelectorAll('[data-tile-group="' + group + '"]').forEach(function (t) {
          t.classList.remove('is-selected');
          t.setAttribute('aria-checked', 'false');
        });
        this.classList.add('is-selected');
        this.setAttribute('aria-checked', 'true');
        var color = this.dataset.tileValue;
        if (group === 'candle-color-metallic') {
          state.addons.candles.types.metallic.color = color;
          updateSticky();
        } else if (group === 'candle-color-number') {
          state.addons.candles.types.number.color = color;
          updateSticky();
        }
      });
      tile.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.click(); }
      });
    });
  }

  function bindGiftInput() {
    var ta = document.getElementById('dbmcb-message-text');
    if (!ta) return;
    ta.addEventListener('input', function () { state.addons.message.text = this.value; });
  }

  function bindCandleNumber() {
    var inp = document.getElementById('dbmcb-candle-number');
    if (!inp) return;
    inp.addEventListener('input', function () {
      var cleaned = String(this.value || '').replace(/\D/g, '').slice(0, 2);
      if (cleaned !== this.value) this.value = cleaned;
      state.addons.candles.types.number.number = cleaned;
      updateSticky();
    });
  }

  /* ── Validation ──
     Mirrors signature's pattern — flag specific issues per addon card
     so the customer sees the error on the card itself, not in a
     general "fix it" toast. */
  function validateAddons() {
    var errors = [];

    if (state.addons.candles.selected) {
      var ts = state.addons.candles.types;
      var anyActive = ts.rainbow.active || ts.sparklers.active || ts.metallic.active || ts.number.active;
      if (!anyActive) {
        errors.push({ id: 'dbmcb-addon-card-candles', msg: 'Please choose at least one candle type, or deselect candles.' });
      } else if (ts.metallic.active && !ts.metallic.color) {
        errors.push({ id: 'dbmcb-addon-card-candles', msg: 'Please choose a color for your metallic candles.' });
      } else if (ts.number.active && !ts.number.color) {
        errors.push({ id: 'dbmcb-addon-card-candles', msg: 'Please choose a color for your number candles.' });
      } else if (ts.number.active && (!ts.number.number || !ts.number.number.toString().trim())) {
        errors.push({ id: 'dbmcb-addon-card-candles', msg: 'Please enter the number for your candles.' });
      }
    }

    /* Also validate that both flavors are picked. Use a flavor-section
       error sentinel since slots aren't addon cards. */
    if (!state.flavors.flavor1 || !state.flavors.flavor2) {
      errors.push({ id: 'dbmcb-flavor-error-anchor', msg: 'Please pick both flavors.' });
    }

    /* Dietary acknowledgment errors are caught upstream in
       bindStickyBtn so we can open the collapsed section and scroll
       to it — better UX than flashing an error on something the
       customer can't see. */

    return errors;
  }

  function showValidationErrors(errors) {
    document.querySelectorAll('.dbsb-addon-error').forEach(function(el) {
      el.style.display = 'none'; el.textContent = '';
    });
    document.querySelectorAll('.dbsb-addon-card.has-error').forEach(function(el) {
      el.classList.remove('has-error');
    });

    if (errors.length === 0) return true;

    var first = errors[0];
    errors.forEach(function(err) {
      var card = document.getElementById(err.id);
      if (!card) return;
      card.classList.add('has-error');
      var errEl = card.querySelector('.dbsb-addon-error');
      if (!errEl) {
        errEl = document.createElement('div');
        errEl.className = 'dbsb-addon-error';
        card.appendChild(errEl);
      }
      errEl.textContent = err.msg;
      errEl.style.display = 'block';
    });

    var firstCard = document.getElementById(first.id);
    if (firstCard) firstCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return false;
  }

  /* ── Sticky ATC ── */
  function bindStickyBtn() {
    var btn = document.getElementById('dbmcb-sticky-btn');
    if (!btn) return;
    btn.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      if (!state.variantId) return;
      if (!state.flavors.flavor1 || !state.flavors.flavor2) {
        /* Reopen flavors section so the customer sees what's missing. */
        openSection('flavors');
        var section = document.getElementById('dbmcb-section-flavors');
        if (section) section.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      /* If a dietary toggle is on but the ack isn't checked, open the
         dietary section and scroll to it instead of just flashing an
         error on a collapsed accordion the customer can't see. */
      var needsDietAck =
        (state.diet.gf  && !state.diet.gfAcknowledged) ||
        (state.diet.nut && !state.diet.nutAcknowledged);
      if (needsDietAck) {
        openSection('diet');
        var dietSection = document.getElementById('dbmcb-section-diet');
        if (dietSection) dietSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      var errors = validateAddons();
      if (!showValidationErrors(errors)) return;
      submitCart();
    });
  }

  /* ── Cart submit — two-step, mirrors signature ──
     Step 1: cupcake product (with all flavor + message props) alone.
     Step 2: candle add-ons, each tagged with the cupcake line's
             unique key (_addon_for) so identical add-ons across
             multiple cupcake orders don't merge into qty 2.
     Step 3: verify totals + redirect to /cart. */
  function submitCart() {
    var btn = document.getElementById('dbmcb-sticky-btn');
    if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }

    var expectedTotal = calcTotal();

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: state.variantId, quantity: 1, properties: buildProperties() }]
      })
    })
    .then(function (r) {
      return r.json().then(function (data) {
        return { ok: r.ok, status: r.status, data: data };
      });
    })
    .then(function (res) {
      if (!res.ok) {
        if (btn) { btn.textContent = 'Add to cart'; btn.disabled = false; }
        console.error('dbmcb cupcake-add error:', res.status, res.data);
        var msg = (res.data && (res.data.description || res.data.message)) || 'Please try again.';
        alert('Something went wrong: ' + msg);
        return;
      }

      var cupcakeKey = res.data && res.data.items && res.data.items[0] && res.data.items[0].key
                     ? res.data.items[0].key
                     : null;
      var addons = buildAddonItems(cupcakeKey);

      if (!addons.length) {
        verifyAndFinish(btn, expectedTotal);
        return;
      }

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: addons })
      })
      .then(function (r) {
        return r.json().then(function (data) {
          return { ok: r.ok, status: r.status, data: data };
        });
      })
      .then(function (addonRes) {
        if (!addonRes.ok) {
          console.error('dbmcb addon-add error:', addonRes.status, addonRes.data);
          var amsg = (addonRes.data && (addonRes.data.description || addonRes.data.message)) || 'Some extras couldn\'t be added.';
          alert('Your cupcakes were added, but: ' + amsg + ' Please review your cart.');
          if (btn) btn.textContent = 'Added (with issues)';
          setTimeout(function () { window.location.href = '/cart'; }, 600);
          return;
        }
        verifyAndFinish(btn, expectedTotal);
      });
    })
    .catch(function (err) {
      console.error('dbmcb cart error:', err);
      if (btn) { btn.textContent = 'Add to cart'; btn.disabled = false; }
      alert('We couldn\'t reach the cart. Check your connection and try again.');
    });
  }

  function verifyAndFinish(btn, expectedTotal) {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var cupcakeItems = cart.items.filter(function (i) {
          return !(i.properties && i.properties._addon_for);
        });
        var latest = cupcakeItems[cupcakeItems.length - 1];
        var cupcakeKey = latest ? latest.key : null;
        var related = cart.items.filter(function (i) {
          return i.key === cupcakeKey || (i.properties && i.properties._addon_for === cupcakeKey);
        });
        var actualTotal = related.reduce(function (s, i) { return s + (i.line_price / 100); }, 0);

        if (Math.abs(expectedTotal - actualTotal) > 0.02) {
          console.warn(
            'dbmcb price mismatch — promised $' + expectedTotal.toFixed(2) +
            ', cart shows $' + actualTotal.toFixed(2) +
            ' (diff $' + (expectedTotal - actualTotal).toFixed(2) + '). ' +
            'Some add-on variant likely failed to resolve.'
          );
          related.forEach(function (i) {
            console.log('  · ' + i.product_title + ' — ' + (i.variant_title || '') + ' × ' + i.quantity + ' = $' + (i.line_price / 100).toFixed(2));
          });
        }
        onCartSuccess(btn);
      })
      .catch(function (err) {
        console.error('dbmcb cart verify error:', err);
        onCartSuccess(btn);
      });
  }

  function onCartSuccess(btn) {
    if (btn) btn.textContent = 'Added! ✓';
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:change',  { bubbles: true }));
    setTimeout(function () { window.location.href = '/cart'; }, 400);
  }

  /* ── Build add-on line items — candles + GF upcharge ──
     GF gets its own cart line so the customer sees the $15 charge
     itemized, and so order fulfillment workflows know which order
     needs the GF treatment. Same pattern as signature builder. */
  function buildAddonItems(cupcakeKey) {
    var items = [];
    var tag = cupcakeKey ? { '_addon_for': cupcakeKey } : {};

    if (state.diet.gf && ADDON_IDS.glutenFree) {
      items.push({
        id: ADDON_IDS.glutenFree,
        quantity: 1,
        properties: Object.assign({ '_addon': 'Gluten-free' }, tag)
      });
    }

    if (state.addons.candles.selected) {
      var ts = state.addons.candles.types;
      if (ts.rainbow.active && ADDON_IDS.rainbowCandles) {
        items.push({
          id: ADDON_IDS.rainbowCandles,
          quantity: 1,
          properties: Object.assign({ '_addon': 'Rainbow candles' }, tag)
        });
      }
      if (ts.sparklers.active && ADDON_IDS.sparklers) {
        items.push({
          id: ADDON_IDS.sparklers,
          quantity: 1,
          properties: Object.assign({ '_addon': 'Sparklers' }, tag)
        });
      }
      if (ts.metallic.active && ts.metallic.color) {
        var mid = METALLIC_IDS[ts.metallic.color];
        if (mid) {
          items.push({
            id: mid,
            quantity: 1,
            properties: Object.assign({ '_addon': 'Metallic candles (' + ts.metallic.color + ')' }, tag)
          });
        } else {
          console.warn('dbmcb: metallic candle variant not found for color:', ts.metallic.color);
        }
      }
      if (ts.number.active && ts.number.color && ts.number.number) {
        var nid = CANDLE_IDS[ts.number.color];
        var numDigits = String(ts.number.number).replace(/\D/g, '').length || 1;
        if (nid) {
          items.push({
            id: nid,
            quantity: numDigits,
            properties: Object.assign({
              'Number': ts.number.number,
              '_addon': 'Number candles (' + ts.number.color + ')'
            }, tag)
          });
        } else {
          console.warn('dbmcb: number candle variant not found for color:', ts.number.color);
        }
      }
    }
    return items;
  }

  /* ── Build cart properties for the cupcake line ──
     Per spec: flavor names tracked in line item properties as
     "Vanilla × 12, Chocolate × 12". Gift message stored as
     "Gift message" property — same key as signature so order
     fulfillment workflows stay consistent. */
  function buildProperties() {
    var props = {};
    var f1 = state.flavors.flavor1, f2 = state.flavors.flavor2;

    if (f1 && f2) {
      if (f1.name === f2.name) {
        /* Both slots same flavor → just show "Vanilla × 24" rather
           than "Vanilla × 12, Vanilla × 12" which looks like a typo. */
        props['Flavors'] = f1.name + ' × 24';
      } else {
        props['Flavors'] = f1.name + ' × 12, ' + f2.name + ' × 12';
      }
    }

    /* Dietary preferences — formatted the same as signature builder
       so order fulfillment workflows stay consistent across products. */
    var dietParts = [];
    if (state.diet.gf)  dietParts.push('Gluten-Free (+$15.00)');
    if (state.diet.nut) dietParts.push('Nut Allergy (acknowledged)');
    if (dietParts.length > 0) props['Dietary'] = dietParts.join(' + ');

    if (state.addons.message.selected && state.addons.message.text && state.addons.message.text.trim()) {
      props['Gift message'] = state.addons.message.text;
    }

    if (state.addons.candles.selected) {
      var ts = state.addons.candles.types;
      var parts = [];
      if (ts.rainbow.active)   parts.push('Rainbow candles');
      if (ts.sparklers.active) parts.push('Sparklers');
      if (ts.metallic.active && ts.metallic.color) parts.push('Metallic candles (' + ts.metallic.color + ')');
      if (ts.number.active && ts.number.color && ts.number.number) {
        parts.push('Number candles (' + ts.number.color + ') — #' + ts.number.number);
      }
      if (parts.length) props['Candles'] = parts.join(' + ');
    }
    return props;
  }

  /* ── Total calculation ──
     Cupcakes have a single fixed price (no size variants). Only
     add-ons modify the total. */
  function calcTotal() {
    var total = state.basePrice;
    if (state.diet.gf) total += 15;  /* GF upcharge matches signature cake builder */
    if (state.addons.candles.selected) {
      var ts = state.addons.candles.types;
      if (ts.rainbow.active)   total += 4.99;
      if (ts.sparklers.active) total += 11.99;
      if (ts.metallic.active && ts.metallic.color) total += 4.99;
      if (ts.number.active && ts.number.color && ts.number.number) {
        var digits = String(ts.number.number).replace(/\D/g, '').length || 0;
        total += digits * 2;
      }
    }
    return total;
  }

  function updateSticky() {
    var total   = calcTotal();
    var nameEl  = document.getElementById('dbmcb-sticky-name');
    var priceEl = document.getElementById('dbmcb-sticky-price');
    var liveEl  = document.getElementById('dbmcb-sticky-live');
    var btn     = document.getElementById('dbmcb-sticky-btn');

    /* Sticky name shows the selected flavors once both picked, else
       a "pick your flavors" prompt. Keeps the customer oriented as
       they scroll. */
    var nameText;
    if (state.flavors.flavor1 && state.flavors.flavor2) {
      if (state.flavors.flavor1.name === state.flavors.flavor2.name) {
        nameText = state.flavors.flavor1.name + ' × 24';
      } else {
        nameText = state.flavors.flavor1.name + ' + ' + state.flavors.flavor2.name;
      }
    } else if (state.flavors.flavor1) {
      nameText = state.flavors.flavor1.name + ' + pick one more';
    } else {
      nameText = 'Choose your flavors above';
    }

    if (nameEl)  nameEl.textContent  = nameText;
    if (priceEl) priceEl.textContent = '$' + total.toFixed(2);
    if (liveEl) liveEl.textContent = nameText + ' — $' + total.toFixed(2);
    if (btn) {
      var ready = !!(state.flavors.flavor1 && state.flavors.flavor2 && state.variantId);
      btn.classList.toggle('is-active', ready);
      if (btn.textContent === 'Adding...' || btn.textContent === 'Added! ✓' || btn.textContent === 'Added (with issues)') {
        /* In-flight state — don't overwrite. */
      } else {
        btn.textContent = 'Add to cart';
      }
    }
  }

  function markSectionDone(id) {
    var num = document.getElementById('dbmcb-num-' + id);
    if (num) num.classList.add('is-done');
  }

})();

/* ── Cart badge count update ──
   Identical pattern to signature builder. Counts only non-addon
   items as "cupcake orders" so the badge reflects real orders,
   not the candle/sparkler companion lines. */
(function () {
  var EVENTS = ['cart:refresh', 'cart:change', 'cart:update', 'cart-update'];
  var lastCount = -1;

  function refreshBadge() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var cupcakeCount = 0;
        cart.items.forEach(function (item) {
          var isAddon = false;
          if (item.properties) {
            Object.keys(item.properties).forEach(function (key) {
              if (key === '_addon' || key === '_addon_for') isAddon = true;
            });
          }
          if (!isAddon) cupcakeCount += item.quantity;
        });

        if (cupcakeCount === lastCount) return;
        lastCount = cupcakeCount;

        var badges = document.querySelectorAll('.dbn-cart-badge');
        badges.forEach(function (el) {
          el.textContent = cupcakeCount;
          el.style.display = cupcakeCount > 0 ? '' : 'none';
        });
        var bubbles = document.querySelectorAll('.cart-count-bubble span:not(.visually-hidden)');
        bubbles.forEach(function (el) { el.textContent = cupcakeCount; });
      })
      .catch(function () { /* silent */ });
  }

  if (document.readyState === 'complete') {
    refreshBadge();
  } else {
    window.addEventListener('load', refreshBadge);
  }
  EVENTS.forEach(function (evt) {
    document.addEventListener(evt, refreshBadge);
  });
})();